import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../utils/api';

import { getDriveApiToken } from '../../lib/auth-token';
export type ItemType = 'file' | 'folder';

interface FavoriteItem {
  id: string;
  name: string;
  type: ItemType;
  size?: number;
  mimetype?: string;
  updatedAt: string;
  thumbnailUrl?: string;
  itemCount?: number;
  isStarred: boolean;
}

interface FavoritesState {
  items: FavoriteItem[];
  loading: boolean;
  error: string | null;
}

const initialState: FavoritesState = {
  items: [],
  loading: false,
  error: null,
};

// Helper to configure axios with token
const getAxiosConfig = () => {
  const token = getDriveApiToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const fetchStarredItems = createAsyncThunk(
  'favorites/fetchStarredItems',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/folders/special/favorites', getAxiosConfig());
      // Backend returns { folders: [], files: [] }
      const folders = response.data.folders || [];
      const files = response.data.files || [];
      
      // Transform folders to have consistent format with files
      const transformedFolders = folders.map((folder: any) => ({
        ...folder,
        type: 'folder',
        mimetype: 'folder'
      }));
      
      // Combine folders and files
      return [...transformedFolders, ...files];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch favorite items');
    }
  }
);

export const starItem = createAsyncThunk(
  'favorites/starItem',
  async ({ id, type }: { id: string; type: ItemType }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/folders/special/favorites/${id}`, { itemType: type, isStarred: true }, getAxiosConfig());
      return { id, type, ...response.data.item };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to star item');
    }
  }
);

export const unstarItem = createAsyncThunk(
  'favorites/unstarItem',
  async ({ id, type }: { id: string; type: ItemType }, { rejectWithValue }) => {
    try {
      // Explicit state makes repeated requests idempotent.
      await api.put(`/folders/special/favorites/${id}`, { itemType: type, isStarred: false }, getAxiosConfig());
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to unstar item');
    }
  }
);

const favoriteSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    clearFavoriteError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Starred Items
      .addCase(fetchStarredItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStarredItems.fulfilled, (state, action: PayloadAction<FavoriteItem[]>) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchStarredItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Star Item
      .addCase(starItem.fulfilled, (state, action: PayloadAction<FavoriteItem>) => {
        const newItem = action.payload;
        if (!state.items.some(item => item.id === newItem.id)) {
          state.items.push(newItem);
        }
      })
      .addCase(starItem.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Unstar Item
      .addCase(unstarItem.fulfilled, (state, action: PayloadAction<string>) => {
        const id = action.payload;
        state.items = state.items.filter(item => item.id !== id);
      })
      .addCase(unstarItem.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearFavoriteError } = favoriteSlice.actions;
export default favoriteSlice.reducer; 